import { useState, useRef, useCallback } from 'react';

const THREAD_KEY = 'wwi_current_thread';

/**
 * useCurrentThread - Manages the "current thread" tracking system
 * Tracks which task the user was last working on and handles orientation display
 */
export const useCurrentThread = () => {
  const [currentThreadId, setCurrentThreadId] = useState(() => 
    localStorage.getItem(THREAD_KEY)
  );
  const [showOrientationStrip, setShowOrientationStrip] = useState(false);
  const [showDimOverlay, setShowDimOverlay] = useState(false);
  const [orientationText, setOrientationText] = useState("");
  const [highlightedCardId, setHighlightedCardId] = useState(null);
  const orientationTimerRef = useRef(null);
  const hasShownInitialStrip = useRef(false);

  // Update the current thread when user interacts with a card
  const updateCurrentThread = useCallback((cardId) => {
    setCurrentThreadId(cardId);
    localStorage.setItem(THREAD_KEY, cardId);
  }, []);

  // Clear the current thread
  const clearCurrentThread = useCallback(() => {
    setCurrentThreadId(null);
    localStorage.removeItem(THREAD_KEY);
  }, []);

  // Show orientation with full card info and scrolling
  const showThreadOrientation = useCallback((cardContent, cardId, shouldScroll = true) => {
    if (orientationTimerRef.current) clearTimeout(orientationTimerRef.current);

    if (!cardContent) {
      // No thread found - show empty orientation briefly
      setOrientationText("");
      setShowOrientationStrip(true);
      orientationTimerRef.current = setTimeout(() => setShowOrientationStrip(false), 3000);
      return;
    }

    setOrientationText(cardContent);
    setShowOrientationStrip(true);
    setShowDimOverlay(true);
    setHighlightedCardId(cardId);

    // Scroll to the card
    if (shouldScroll) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="card-${cardId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 150);
    }

    // Auto-hide after 4 seconds
    orientationTimerRef.current = setTimeout(() => {
      setShowOrientationStrip(false);
      setShowDimOverlay(false);
    }, 4000);
    
    // Clear highlight slightly before overlay
    setTimeout(() => setHighlightedCardId(null), 3500);
  }, []);

  // Dismiss orientation manually
  const dismissOrientation = useCallback(() => {
    if (orientationTimerRef.current) {
      clearTimeout(orientationTimerRef.current);
    }
    setShowOrientationStrip(false);
    setTimeout(() => {
      setShowDimOverlay(false);
      setHighlightedCardId(null);
    }, 300);
  }, []);

  // Mark initial strip as shown
  const markInitialStripShown = useCallback(() => {
    hasShownInitialStrip.current = true;
  }, []);

  // Check if initial strip was shown
  const wasInitialStripShown = useCallback(() => {
    return hasShownInitialStrip.current;
  }, []);

  return {
    currentThreadId,
    updateCurrentThread,
    clearCurrentThread,
    showOrientationStrip,
    showDimOverlay,
    orientationText,
    highlightedCardId,
    setHighlightedCardId,
    showThreadOrientation,
    dismissOrientation,
    markInitialStripShown,
    wasInitialStripShown,
  };
};

export default useCurrentThread;
